import { AutoLinkNode, LinkNode } from "@lexical/link"
import { ListItemNode, ListNode } from "@lexical/list"
import { AutoLinkPlugin, createLinkMatcherWithRegExp } from "@lexical/react/LexicalAutoLinkPlugin"
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin"
import { ListPlugin } from "@lexical/react/LexicalListPlugin"
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin"
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin"
import { HeadingNode } from "@lexical/rich-text"
import { $getNearestNodeOfType } from "@lexical/utils"
import { $getRoot, type EditorState, type LexicalEditor as LexicalEditorType } from "lexical"
import { useRef, useState, type FC } from "react"
import { useDebouncedCallback } from "use-debounce"

import { isEditorStateEmpty } from "@forento/shared/utilities/lexical"
import { emailRegex, urlRegex } from "@forento/shared/utilities/regex"

import LexicalToolbarPlugin from "./LexicalToolbarPlugin"

export { LexicalEditorType }

const matchers = [
	createLinkMatcherWithRegExp(urlRegex, text => {
		if (text.startsWith("www")) return `https://${text}`
		return text
	}),
	createLinkMatcherWithRegExp(emailRegex, text => {
		return `mailto:${text}`
	}),
]

type Props = {
	editorRef(element: LexicalEditorType): void
	onEmptyChange?(isEmpty: boolean): void
	extendedFormatting?: boolean
}
const LexicalEditor: FC<Props> = ({ editorRef, onEmptyChange, extendedFormatting = false }) => {
	const [hasContentHeadings, setHasContentHeadings] = useState(false)

	const isEmptyCache = useRef(true)

	const checkHeadings = useDebouncedCallback((state: EditorState) => {
		const hasHeadings = state.read<boolean>(() => {
			const root = $getRoot()
			const children = root.getChildren()

			for (const child of children) {
				if (child instanceof HeadingNode) return true

				const headingNode = $getNearestNodeOfType<HeadingNode>(child, HeadingNode)
				if (headingNode !== null) return true
			}

			return false
		})

		if (hasHeadings !== hasContentHeadings) {
			setHasContentHeadings(hasHeadings)
		}
	}, 1_000)

	return (
		<LexicalComposer
			initialConfig={{
				namespace: "Editor",
				theme: {
					heading: {
						h2: "lexical-heading",
					},
					text: {
						bold: "lexical-bold",
						italic: "lexical-italic",
						underline: "lexical-underline",
						strikethrough: "lexical-strikethrough",
						underlineStrikethrough: "lexical-underline-strikethrough",
						code: "lexical-code",
					},
					list: {
						ol: "lexical-ol",
						ul: "lexical-ul",
					},
					link: "lexical-link",
				},
				nodes: [ListNode, ListItemNode, HeadingNode, LinkNode, AutoLinkNode],
				onError(error) {
					console.error(error)
				},
			}}
		>
			<RichTextPlugin
				placeholder={null}
				ErrorBoundary={LexicalErrorBoundary}
				contentEditable={<ContentEditable className="lexical" style={{ outline: "none" }} />}
			/>
			<EditorRefPlugin editorRef={editorRef} />
			<OnChangePlugin
				onChange={state => {
					if (onEmptyChange) {
						const isEmpty = isEditorStateEmpty(state)
						if (isEmpty !== isEmptyCache.current) {
							isEmptyCache.current = isEmpty
							onEmptyChange(isEmpty)
						}
					}
					checkHeadings(state)
				}}
			/>
			<ClearEditorPlugin />
			<LexicalToolbarPlugin headings={extendedFormatting || hasContentHeadings} colors={extendedFormatting} />
			<ListPlugin />
			<AutoLinkPlugin matchers={matchers} />
			<LinkPlugin />
			<HistoryPlugin />
		</LexicalComposer>
	)
}

export default LexicalEditor
